/**
 * 通用数据请求 Hook。
 *
 * 封装 loading / error / data / refetch 状态，配合 services 层使用。
 *
 * @module hooks/useApi
 */
import { useCallback, useEffect, useRef, useState, type DependencyList } from 'react';

/** useApi 返回值。 */
export interface UseApiResult<T> {
  /** 请求返回的数据（未返回前为 null）。 */
  data: T | null;
  /** 是否加载中。 */
  loading: boolean;
  /** 错误信息（无错误为 null）。 */
  error: Error | null;
  /** 重新发起请求。 */
  refetch: () => Promise<void>;
}

/**
 * 通用请求 Hook。
 *
 * @typeParam T 返回值类型。
 * @param fetcher 返回 `Promise<T>` 的请求函数（通常来自 services）。
 * @param deps 触发重新请求的依赖（默认空数组，仅挂载时请求一次）。
 * @param immediate 是否在挂载时立即请求，默认 true。
 * @returns 请求状态与数据。
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: DependencyList = [],
  immediate = true,
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(immediate);
  const [error, setError] = useState<Error | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetcherRef.current();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('请求失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!immediate) return;
    void refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch };
}
